export type ComicTemplateType = 'single' | 'double' | 'custom'

export interface DialogBubbleData {
  id: string
  type: 'speech' | 'thought' | 'shout' | 'explode'
  text: string
  x: number
  y: number
  width: number
  height: number
  fontSize: number
  fontFamily: string
  color: string
  backgroundColor: string
  borderColor: string
  borderWidth: number
  borderRadius: number
  padding: number
  rotation: number
  sequence: number | null
  panelId: string // 所属面板ID
  arrowDirection?: 'down' | 'up'
}

export interface ComicPanel {
  id: string
  x: number
  y: number
  width: number
  height: number
  backgroundColor: string
  borderColor: string
  borderWidth: number
  borderRadius: number
  backgroundImage?: string // 背景图片URL
  title?: string // 面板标题
  order: number // 排序
  aspectRatio?: number // 新增：宽高比
}

export interface ComicTemplate {
  id: string
  name: string
  type: ComicTemplateType
  panels: ComicPanel[]
  width: number
  height: number
  isCustom?: boolean // 是否为自定义模板
}

export interface ComicCanvasData {
  id: string
  template: ComicTemplate
  panels: ComicPanel[]
  dialogBubbles: DialogBubbleData[]
  width: number
  height: number
  backgroundColor: string
  title?: string
  createdAt: Date
  updatedAt: Date
}

export interface ExportOptions {
  format: 'png' | 'jpg' | 'svg'
  quality: number
  width: number
  height: number
  includeBackground: boolean
}

export interface ImageAsset {
  id: string
  url: string // dataURL
  name: string
}

// 面板布局计算辅助函数
export const calculatePanelLayout = (
  panelCount: number, 
  canvasWidth: number, 
  canvasHeight: number,
  layoutType: 'vertical' | 'grid' | 'horizontal' = 'vertical',
  aspectRatio: number = 4 / 3 // 默认4:3
): ComicPanel[] => {
  const panels: ComicPanel[] = []
  const margin = 20
  const panelSpacing = 15

  if (layoutType === 'vertical') {
    // 垂直布局：面板垂直排列
    const panelWidth = canvasWidth - (margin * 2)
    // 计算理想高度，确保不会太扁
    let panelHeight = Math.max(panelWidth / aspectRatio, 150) // 最小高度150px
    
    // 计算总高度
    let totalHeight = panelCount * panelHeight + (panelCount - 1) * panelSpacing + margin * 2
    
    // 如果超出画布高度，按比例缩减，但保持最小高度
    if (totalHeight > canvasHeight) {
      const availableHeight = canvasHeight - margin * 2 - (panelCount - 1) * panelSpacing
      panelHeight = Math.max(availableHeight / panelCount, 120) // 最小高度120px
    }
    
    for (let i = 0; i < panelCount; i++) {
      panels.push({
        id: `panel-${i + 1}`,
        x: margin,
        y: margin + i * (panelHeight + panelSpacing),
        width: panelWidth,
        height: panelHeight,
        backgroundColor: '#f8f9fa',
        borderColor: '#2d3748',
        borderWidth: 3,
        borderRadius: 16,
        order: i + 1,
        aspectRatio: panelWidth / panelHeight
      })
    }
  } else if (layoutType === 'grid') {
    // 网格布局：面板网格排列，固定2列布局
    const cols = 2 // 固定双列布局
    const rows = Math.ceil(panelCount / cols)
    
    // 计算面板尺寸，确保不会太小
    const availableWidth = canvasWidth - (margin * 2) - ((cols - 1) * panelSpacing)
    const availableHeight = canvasHeight - (margin * 2) - ((rows - 1) * panelSpacing)
    
    let panelWidth = Math.max(availableWidth / cols, 180) // 增加最小宽度到180px
    let panelHeight = Math.max(availableHeight / rows, 135) // 增加最小高度到135px
    
    // 确保面板不会超出画布边界
    panelWidth = Math.min(panelWidth, availableWidth / cols)
    panelHeight = Math.min(panelHeight, availableHeight / rows)
    
    // 尝试保持合理的宽高比，但不强制
    const currentRatio = panelWidth / panelHeight
    if (currentRatio > 2.5) {
      // 太宽了，适当调整高度
      panelHeight = Math.max(panelWidth / 2, 135)
    } else if (currentRatio < 0.6) {
      // 太窄了，适当调整宽度  
      panelWidth = Math.max(panelHeight * 0.8, 180)
    }
    
    for (let i = 0; i < panelCount; i++) {
      const row = Math.floor(i / cols)
      const col = i % cols
      panels.push({
        id: `panel-${i + 1}`,
        x: margin + col * (panelWidth + panelSpacing),
        y: margin + row * (panelHeight + panelSpacing),
        width: panelWidth,
        height: panelHeight,
        backgroundColor: '#f8f9fa',
        borderColor: '#2d3748',
        borderWidth: 3,
        borderRadius: 16,
        order: i + 1,
        aspectRatio: panelWidth / panelHeight
      })
    }
  } else if (layoutType === 'horizontal') {
    // 水平布局：面板水平排列
    const panelHeight = canvasHeight - (margin * 2)
    // 计算理想宽度，确保不会太窄
    let panelWidth = Math.max(panelHeight * aspectRatio, 150) // 最小宽度150px
    
    // 计算总宽度
    let totalWidth = panelCount * panelWidth + (panelCount - 1) * panelSpacing + margin * 2
    
    // 如果超出画布宽度，按比例缩减，但保持最小宽度
    if (totalWidth > canvasWidth) {
      const availableWidth = canvasWidth - margin * 2 - (panelCount - 1) * panelSpacing
      panelWidth = Math.max(availableWidth / panelCount, 120) // 最小宽度120px
    }
    
    for (let i = 0; i < panelCount; i++) {
      panels.push({
        id: `panel-${i + 1}`,
        x: margin + i * (panelWidth + panelSpacing),
        y: margin,
        width: panelWidth,
        height: panelHeight,
        backgroundColor: '#f8f9fa',
        borderColor: '#2d3748',
        borderWidth: 3,
        borderRadius: 16,
        order: i + 1,
        aspectRatio: panelWidth / panelHeight
      })
    }
  }
  return panels
}

// 预设的条漫模板
export const COMIC_TEMPLATES: ComicTemplate[] = [
  {
    id: 'single-vertical',
    name: '单排竖版',
    type: 'single',
    width: 400,
    height: 1200,
    panels: calculatePanelLayout(4, 400, 1200, 'vertical')
  },
  {
    id: 'double-grid',
    name: '双排网格',
    type: 'double',
    width: 800,
    height: 600,
    panels: calculatePanelLayout(4, 800, 600, 'grid')
  }
] 